import React, { useEffect, useState } from 'react';
import {
  Box,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Typography,
  MenuItem,
  FormControlLabel,
  Switch,
  Paper,
  IconButton,
  Tooltip,
} from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import RefreshIcon from '@mui/icons-material/Refresh';
import SportsCricketIcon from '@mui/icons-material/SportsCricket';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import HistoryIcon from '@mui/icons-material/History';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import Grid from '@mui/material/GridLegacy';
import { DataGrid, type GridColDef } from '@mui/x-data-grid';
import { useFormik } from 'formik';
import * as yup from 'yup';
import { matchApi, seriesApi, articleApi } from '../../services/api';
import SeriesAutocomplete from '../../components/Cricket/SeriesAutocomplete';
import GroundAutocomplete from '../../components/Cricket/GroundAutocomplete';
import type { Match, Series, Venue, Team, Ground, PaginatedResponse } from '../../models';
import { Link, useNavigate } from 'react-router-dom';
import GroupIcon from '@mui/icons-material/Group';
import { delphiService } from '../../services/delphiApi';
import TeamAutocomplete from '../../components/Cricket/TeamAutocomplete';
import AuditLogViewer from '../../components/AuditLogViewer';

interface FilterState {
  format: string;
  status: string;
  series: Series | null;
  team: Team | null;
  ground: Ground | null;
  ordering: string;
  from_date?: string;
  to_date?: string;
  search?: string;
}

interface MatchFormValues {
  id: number;
  series: Series;
  match_format: string;
  match_status: string;
  start_date: string;
  lineup_announced: boolean;
  venue: { id: number } | null;
  match_result: string | null;
  score_algorithm_enabled: boolean;
  fp_available: boolean;
  is_substitute_allowed: boolean;
  is_e_coverage: boolean;
}

const validationSchema = yup.object({
  series: yup.object().shape({
    id: yup.number().required('Series is required'),
  }).required('Series is required'),
  match_format: yup.string().required('Match format is required'),
  match_status: yup.string().required('Match status is required'),
  start_date: yup.string().required('Start date is required'),
  lineup_announced: yup.boolean(),
  venue: yup.object().shape({
    id: yup.number().required('Venue is required'),
  }).nullable(),
  match_result: yup.string().nullable(),
  score_algorithm_enabled: yup.boolean(),
  fp_available: yup.boolean(),
  is_substitute_allowed: yup.boolean(),
  is_e_coverage: yup.boolean(),
});

const statusOptions = [
  { value: 'Not Started', label: 'Not Started' },
  { value: 'In Progress', label: 'In Progress' },
  { value: 'Completed', label: 'Completed' },
  { value: 'Abandoned', label: 'Abandoned' },
  { value: 'Cancelled', label: 'Cancelled' },
];

const matchFormatOptions = [
  { value: 'T20', label: 'T20' },
  { value: 'T10', label: 'T10' },
  { value: 'ODI', label: 'ODI' },
  { value: 'TEST', label: 'Test' },
  { value: 'HUNDRED_BALL', label: 'Hundred' },
  { value: 'SIXES', label: 'Sixes' },
];

const Matches: React.FC = () => {
  const [matches, setMatches] = useState<Match[]>([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [selectedMatch, setSelectedMatch] = useState<Match | null>(null);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);
  const [articlesByMatch, setArticlesByMatch] = useState<Record<string, { id: number; title: string; slug: string; is_draft: boolean; published: boolean }[]>>({});
  const [sessionsByMatch, setSessionsByMatch] = useState<Record<string, number>>({});
  const [totalCount, setTotalCount] = useState(0);
  const queryParams = new URLSearchParams(window?.location.search);
  const statusParam = queryParams.get("status");
  const seriesId = queryParams.get("series");
  const teamId = queryParams.get("team");
  const groundId = queryParams.get("ground");
  
  const [filters, setFilters] = useState<FilterState>({
    format: '',
    status: statusParam || '',
    series: null,
    team: null,
    ground: null,
    ordering: '',
    search: '',
  });
  const [auditLogOpen, setAuditLogOpen] = useState(false);
  const [auditLogMatch, setAuditLogMatch] = useState<Match | null>(null);

  const navigate = useNavigate();

  // Check URL parameters when component mounts
  useEffect(() => {
    const fetchInitialFilters = async () => {
      try {
        setLoading(true);
        const filtersToUpdate = { ...filters };
        
        // Fetch series details if seriesId is in the URL
        if (seriesId) {
          try {
            const seriesResponse = await seriesApi.getById(Number(seriesId));
            if (seriesResponse.data) {
              filtersToUpdate.series = seriesResponse.data;
            }
          } catch (error) {
            console.error('Error fetching series details:', error);
          }
        }
        
        // Fetch team details if teamId is in the URL
        if (teamId) {
          try {
            const { teamApi } = await import('../../services/api');
            const teamResponse = await teamApi.getById(Number(teamId));
            if (teamResponse.data) {
              filtersToUpdate.team = teamResponse.data;
            }
          } catch (error) {
            console.error('Error fetching team details:', error);
          }
        }
        
        // Fetch ground details if groundId is in the URL
        if (groundId) {
          try {
            const { groundApi } = await import('../../services/api');
            const groundResponse = await groundApi.getById(Number(groundId));
            if (groundResponse.data) {
              filtersToUpdate.ground = groundResponse.data;
            }
          } catch (error) {
            console.error('Error fetching ground details:', error);
          }
        }
        
        setFilters(filtersToUpdate);
      } catch (error) {
        console.error('Error fetching initial filters:', error);
      } finally {
        setLoading(false);
      }
    };
    
    fetchInitialFilters();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [seriesId, teamId, groundId]);  // Dependencies for the URL parameters

  const columns: GridColDef[] = [
    { 
      field: 'id', 
      headerName: 'ID', 
      width: 70, 
      sortable: true,
      filterable: true,
      type: 'string'
    },
    { 
      field: 'name',
      headerName: 'Match',
      minWidth: 200,
      // flex: 0.3,
      renderCell: (params: any) => (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <a
            href={`https://ai11.in/series/${params.row.series?.slug}/match/${params.row.slug}`}
            target="_blank"
            rel="noopener noreferrer"
            style={{ textDecoration: 'none', color: 'inherit', fontWeight: 500 }}
          >
            {params.value}
          </a>
          <IconButton
            href={`https://ai11.in/series/${params.row.series?.slug}/match/${params.row.slug}`}
            target="_blank"
            rel="noopener noreferrer"
            size="small"
            color="primary"
            sx={{ ml: 0.5 }}
          >
            <OpenInNewIcon fontSize="small" />
          </IconButton>
        </Box>
      ),
    },
    { 
      field: 'match_format', 
      headerName: 'Format', 
      width: 70,
      filterable: true,
      type: 'singleSelect',
      valueOptions: matchFormatOptions.map(opt => opt.value)
    },
    { 
      field: 'series',
      headerName: 'Series',
      minWidth: 160,
      renderCell: (params: any) => (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <a
            href={`https://ai11.in/series/${params.row.series?.slug}`}
            target="_blank"
            rel="noopener noreferrer"
            style={{ textDecoration: 'none', color: 'inherit', fontWeight: 500 }}
          >
            {params.row.series?.name}
          </a>
          <IconButton
            href={`https://ai11.in/series/${params.row.series?.slug}`}
            target="_blank"
            rel="noopener noreferrer"
            size="small"
            color="primary"
            sx={{ ml: 0.5 }}
          >
            <OpenInNewIcon fontSize="small" />
          </IconButton>
        </Box>
      ),
    },
    {
      field: 'articles',
      headerName: 'Articles',
      minWidth: 260,
      flex: 1,
      renderCell: (params: any) => {
        const list = articlesByMatch[String(params.row.id)] || [];
        if (!list.length) {
          return <Typography variant="body2" color="text.secondary">No article</Typography>;
        }
        const first = list[0];
        const externalUrl = first.slug ? `https://ai11.in/articles/${first.slug}` : undefined;
        return (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, minWidth: 0 }}>
            <Typography variant="body2" noWrap>{first.title}</Typography>
            {externalUrl && (
              <Tooltip title="Preview">
                <IconButton size="small" href={externalUrl} target="_blank" rel="noopener noreferrer">
                  <OpenInNewIcon fontSize="inherit" />
                </IconButton>
              </Tooltip>
            )}
            <Button size="small" variant="outlined" onClick={() => navigate(`/cms/articles/${first.id}`)}>
              View/Edit
            </Button>
            <Button size="small" variant="text" onClick={() => navigate('/cms/articles/new')}>
              New Draft
            </Button>
          </Box>
        );
      },
    },
    {
      field: 'ai_session',
      headerName: 'AI Session',
      width: 170,
      renderCell: (params: any) => {
        const sessionId = sessionsByMatch[String(params.row.id)];
        return sessionId ? (
          <Button size="small" variant="contained" onClick={() => navigate(`/delphi/${sessionId}`)}>
            Open AI
          </Button>
        ) : (
          <Button
            size="small"
            variant="outlined"
            onClick={async () => {
              try {
                const session = await delphiService.getOrCreateSession({
                  app_label: 'cricket',
                  model: 'Match',
                  object_id: params.row.id,
                });
                navigate(`/delphi/${session.id}`);
              } catch (e) {
                console.error(e);
              }
            }}
          >
            Start AI
          </Button>
        );
      },
    },
    {
      field: 'e_id',
      headerName: 'E ID',
      width: 70,
      sortable: true,
      filterable: true,
      type: 'string',
    },
    {
      field: 'd_id',
      headerName: 'D ID',
      width: 70,
      sortable: true,
      filterable: true,
      type: 'string',
    },
    // {
    //   field: 'mt_id',
    //   headerName: 'MT ID',
    //   width: 70,
    //   sortable: true,
    //   filterable: true,
    //   type: 'string',
    // },
    {
      field: 'c_id',
      headerName: 'C ID',
      width: 70,
      sortable: true,
      filterable: true,
      type: 'string',
    },
    {
      field: 'v_id',
      headerName: 'V ID',
      width: 70,
      sortable: true,
      filterable: true,
      type: 'string',
    },
    // {
    //   field: 'f_id',
    //   headerName: 'F ID',
    //   width: 70,
    //   sortable: true,
    //   filterable: true,
    //   type: 'string',
    // },
    { 
      field: 'start_date', 
      headerName: 'Date', 
      width: 170, 
      sortable: true,
      type: 'dateTime',
      valueGetter: (params: any) => new Date(params),
    },
    { 
      field: 'venue', 
      headerName: 'Venue', 
      width: 160, 
      valueGetter: (venue: Venue) => venue?.name
    },
    {
      field: 'match_status',
      headerName: 'Status',
      width: 100,
      sortable: true,
      filterable: true,
      type: 'singleSelect',
      valueOptions: statusOptions.map(opt => opt.value),
      renderCell: (params) => (
        <Box
          sx={{
            backgroundColor:
              params.value === 'In Progress'
                ? '#ff9800'
                : params.value === 'Completed'
                ? '#4caf50'
                : params.value === 'Abandoned' || params.value === 'Cancelled'
                ? '#f44336'
                : '#2196f3',
            color: 'white',
            padding: '2px 6px',
            fontSize: '0.75rem',
            borderRadius: '10px',
            textTransform: 'capitalize',
            display: 'inline-block',
            minWidth: '60px',
            textAlign: 'center',
          }}
        >
          {params.value}
        </Box>
      ),
    },
    { 
      field: 'match_result', 
      headerName: 'Result', 
      width: 200, 
      sortable: true,
      filterable: false
    },
    {
      field: 'actions',
      headerName: 'Actions',
      width: 250,
      sortable: false,
      filterable: false,
      renderCell: (params: any) => (
        <Box>
          <Tooltip title="View/Edit">
            <IconButton
              color="info"
              size="small"
              component={Link}
              to={`/matches/${params.row.id}/view`}
            >
              <EditIcon />
            </IconButton>
          </Tooltip>
          <Tooltip title="Match Players">
            <IconButton
              color="success"
              size="small"
              onClick={() => navigate(`/match-players?match=${params.row.id}`)}
            >
              <SportsCricketIcon />
            </IconButton>
          </Tooltip>
          <Tooltip title="AI Lineup Analysis">
            <IconButton
              color="secondary"
              size="small"
              onClick={() => navigate(`/ai-lineup?match=${params.row.id}`)}
            >
              <AutoAwesomeIcon />
            </IconButton>
          </Tooltip>
          <Tooltip title="Manage Teams">
            <IconButton
              color="secondary"
              size="small"
              component={Link}
              to={`/matches/${params.row.id}/teams`}
            >
              <GroupIcon />
            </IconButton>
          </Tooltip>
          <Tooltip title="Audit Log">
            <IconButton
              color="info"
              size="small"
              onClick={() => handleViewAuditLog(params.row)}
            >
              <HistoryIcon />
            </IconButton>
          </Tooltip>
          <Tooltip title="Delete">
            <IconButton
              color="error"
              size="small"
              onClick={() => handleDelete(params.row.id)}
            >
              <DeleteIcon />
            </IconButton>
          </Tooltip>
        </Box>
      ),
    },
  ];

  const formik = useFormik<MatchFormValues>({
    initialValues: {
      id: 0,
      series: {} as Series,
      match_format: 'T20',
      match_status: 'Not Started',
      start_date: '',
      lineup_announced: false,
      venue: null,
      match_result: null,
      score_algorithm_enabled: false,
      fp_available: false,
      is_substitute_allowed: false,
      is_e_coverage: true,
    },
    validationSchema,
    onSubmit: async (values) => {
      try {
        if (selectedMatch) {
          await matchApi.update(selectedMatch.id, values);
        } else {
          await matchApi.create(values);
        }
        fetchMatches(page, pageSize);
        handleClose();
      } catch (error) {
        console.error('Error saving match:', error);
      }
    },
  });

  const fetchMatches = React.useCallback(async (pageNumber: number = 1, size: number = pageSize) => {
    try {
      setLoading(true);
      const params: any = {
        page: pageNumber,
        page_size: size,
      };

      if (filters.format) {
        params.match_format = filters.format;
      }
      if (filters.status) {
        params.match_status = filters.status;
      }
      if (filters.series) {
        params.series = filters.series.id;
      }
      if (filters.team) {
        params.teams = filters.team.id;
      }
      if (filters.ground) {
        params.venue = filters.ground.id;
      }
      if (filters.ordering) {
        params.ordering = filters.ordering;
      }
      if (filters.from_date) {
        params.from_date = filters.from_date;
      }
      if (filters.to_date) {
        params.to_date = filters.to_date;
      }
      if (filters.search) {
        params.search = filters.search;
      }

      const response = await matchApi.getAll(params);
      const data = response.data as PaginatedResponse<Match>;
      setMatches(data.results);
      setTotalCount(data.count);

      const ids = (data.results || []).map((m: any) => m.id);
      if (ids.length) {
        articleApi.taggedByMatches(ids).then((d) => setArticlesByMatch(d || {})).catch((error) => {
          console.error('Error fetching articles by matches:', error);
        });
        delphiService.sessionsByObject({ app_label: 'cricket', model: 'Match', object_ids: ids })
          .then((d) => setSessionsByMatch(d || {})).catch((error) => {
            console.error('Error fetching Delphi sessions by matches:', error);
          });
      } else {
        setArticlesByMatch({});
        setSessionsByMatch({});
      }
    } catch (error) {
      console.error('Error fetching matches:', error);
    } finally {
      setLoading(false);
    }
  }, [filters, pageSize]);

  const handleSortModelChange = (model: any) => {
    if (model.length > 0) {
      const { field, sort } = model[0];
      const ordering = sort === 'desc' ? `-${field}` : field;
      setFilters(prev => ({
        ...prev,
        ordering
      }));
    } else {
      setFilters(prev => ({
        ...prev,
        ordering: ''
      }));
    }
  };

  const handleFilterModelChange = (model: any) => {
    const newFilters = { ...filters };
    
    if (model.items) {
      model.items.forEach((item: any) => {
        if (item.field === 'start_date') {
          if (item.operator === '>=') {
            newFilters.from_date = item.value;
          } else if (item.operator === '<=') {
            newFilters.to_date = item.value;
          }
        } else if (item.field === 'match_format') {
          newFilters.format = item.value;
        } else if (item.field === 'match_status') {
          newFilters.status = item.value;
        }
      });
    }

    setFilters(newFilters);
  };

  useEffect(() => {
    fetchMatches(page, pageSize);
  }, [page, pageSize, filters, fetchMatches]);

  const handleFilterChange = (name: keyof FilterState, value: any) => {
    setFilters((prev) => ({
      ...prev,
      [name]: value,
    }));
    setPage(1); // Reset to first page when filters change
    
    // Update URL with query parameters for deep linking
    const searchParams = new URLSearchParams(window.location.search);
    
    if (name === 'series' && value) {
      searchParams.set('series', value.id.toString());
    } else if (name === 'series' && !value) {
      searchParams.delete('series');
    }
    
    if (name === 'team' && value) {
      searchParams.set('team', value.id.toString());
    } else if (name === 'team' && !value) {
      searchParams.delete('team');
    }
    
    if (name === 'ground' && value) {
      searchParams.set('ground', value.id.toString());
    } else if (name === 'ground' && !value) {
      searchParams.delete('ground');
    }
    
    if (name === 'status' && value) {
      searchParams.set('status', value);
    } else if (name === 'status' && !value) {
      searchParams.delete('status');
    }
    
    if (name === 'search' && value) {
      searchParams.set('search', value);
    } else if (name === 'search' && !value) {
      searchParams.delete('search');
    }
    
    const newUrl = window.location.pathname + (searchParams.toString() ? `?${searchParams.toString()}` : '');
    window.history.pushState({}, '', newUrl);
  };

  const handleDelete = async (id: number) => {
    if (window.confirm('Are you sure you want to delete this match?')) {
      try {
        await matchApi.delete(id);
        fetchMatches(page, pageSize);
      } catch (error) {
        console.error('Error deleting match:', error);
      }
    }
  };

  const handleViewAuditLog = (match: Match) => {
    setAuditLogMatch(match);
    setAuditLogOpen(true);
  };

  const handleAuditLogClose = () => {
    setAuditLogOpen(false);
    setAuditLogMatch(null);
  };

  const handleClose = () => {
    setOpen(false);
    setSelectedMatch(null);
    formik.resetForm();
  };

  const handleRefresh = () => {
    fetchMatches(page, pageSize);
  };

  return (
    <Box sx={{ height: "calc(100vh - 240px)", width: "100%" }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
        <Typography variant="h4">Matches</Typography>
        <Box>
          <Tooltip title="Refresh Data">
            <IconButton 
              color="primary" 
              onClick={handleRefresh} 
              sx={{ mr: 1 }}
            >
              <RefreshIcon />
            </IconButton>
          </Tooltip>
          <Button variant="contained" color="primary" onClick={() => setOpen(true)}>
            Add Match
          </Button>
        </Box>
      </Box>

      <Paper sx={{ p: 2, mb: 2 }}>
        <Grid container spacing={2}>
          <Grid item xs={12} sm={6} md={3}>
            <SeriesAutocomplete
              value={filters.series}
              onChange={(value) => handleFilterChange('series', value)}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <TeamAutocomplete
              value={filters.team}
              onChange={(value) => handleFilterChange('team', value)}
              seriesId={filters.series?.id || null}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <GroundAutocomplete
              value={filters.ground}
              onChange={(value) => handleFilterChange('ground', value)}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <TextField
              fullWidth
              select
              label="Status"
              value={filters.status}
              onChange={(e) => handleFilterChange('status', e.target.value)}
              variant="outlined"
              size="small"
            >
              <MenuItem value="">All Statuses</MenuItem>
              {statusOptions.map((option) => (
                <MenuItem key={option.value} value={option.value}>
                  {option.label}
                </MenuItem>
              ))}
            </TextField>
          </Grid>
          <Grid item xs={12} sm={12} md={12}>
            <TextField
              fullWidth
              label="Search (ID, name, series, venue, etc.)"
              value={filters.search || ''}
              onChange={e => handleFilterChange('search', e.target.value)}
              variant="outlined"
              size="small"
              placeholder="Type to search by any field..."
              InputProps={{
                startAdornment: <span role="img" aria-label="search" style={{ marginRight: 8 }}>🔍</span>
              }}
            />
          </Grid>
        </Grid>
      </Paper>

      <DataGrid
        rows={matches}
        columns={columns}
        initialState={{
          pagination: {
            paginationModel: { pageSize: 20, page: 0 },
          },
          columns: {
            columnVisibilityModel: {
              match_result: false,
            },
          },
        }}
        pageSizeOptions={[10, 20, 50, 100]}
        checkboxSelection
        disableRowSelectionOnClick
        loading={loading}
        rowCount={totalCount}
        paginationMode="server"
        sortingMode="server"
        filterMode="server"
        onSortModelChange={handleSortModelChange}
        onFilterModelChange={handleFilterModelChange}
        onPaginationModelChange={(model) => {
          setPage(model.page + 1);
          setPageSize(model.pageSize);
        }}
        sx={{
          '& .MuiDataGrid-cell': {
            py: 1.5,
          },
          '& .MuiDataGrid-columnHeaders': {
            backgroundColor: '#f5f5f5', // Light gray background for header
          },
          '& .MuiDataGrid-row:hover': {
            backgroundColor: '#f0f7ff', // Light blue on hover
          },
          '& .MuiDataGrid-virtualScroller': {
            overflowX: 'scroll',
          }
        }}
        density="compact"
      />

      <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
        <DialogTitle>
          {selectedMatch ? 'Edit Match' : 'Add Match'}
        </DialogTitle>
        <form onSubmit={formik.handleSubmit}>
          <DialogContent>
            <TextField
              fullWidth
              margin="normal"
              name="series.id"
              label="Series ID"
              type="number"
              value={formik.values.series?.id || ''}
              onChange={formik.handleChange}
              error={Boolean(formik.errors.series)}
              helperText={typeof formik.errors.series === 'string' ? formik.errors.series : ''}
            />
            <TextField
              fullWidth
              margin="normal"
              name="match_format"
              label="Match Format"
              select
              value={formik.values.match_format}
              onChange={formik.handleChange}
              error={formik.touched.match_format && Boolean(formik.errors.match_format)}
              helperText={formik.touched.match_format && formik.errors.match_format}
            >
              {matchFormatOptions.map((option) => (
                <MenuItem key={option.value} value={option.value}>
                  {option.label}
                </MenuItem>
              ))}
            </TextField>
            <TextField
              fullWidth
              margin="normal"
              name="match_status"
              label="Status"
              select
              value={formik.values.match_status}
              onChange={formik.handleChange}
              error={formik.touched.match_status && Boolean(formik.errors.match_status)}
              helperText={formik.touched.match_status && formik.errors.match_status}
            >
              {statusOptions.map((option) => (
                <MenuItem key={option.value} value={option.value}>
                  {option.label}
                </MenuItem>
              ))}
            </TextField>
            <TextField
              fullWidth
              margin="normal"
              name="start_date"
              label="Start Date"
              type="datetime-local"
              value={formik.values.start_date}
              onChange={formik.handleChange}
              error={formik.touched.start_date && Boolean(formik.errors.start_date)}
              helperText={formik.touched.start_date && formik.errors.start_date}
              InputLabelProps={{
                shrink: true,
              }}
            />
            <TextField
              fullWidth
              margin="normal"
              name="venue.id"
              label="Venue ID"
              type="number"
              value={formik.values.venue?.id || ''}
              onChange={formik.handleChange}
              error={Boolean(formik.errors.venue)}
              helperText={typeof formik.errors.venue === 'string' ? formik.errors.venue : ''}
            />
            <TextField
              fullWidth
              margin="normal"
              name="match_result"
              label="Result"
              value={formik.values.match_result || ''}
              onChange={formik.handleChange}
              error={formik.touched.match_result && Boolean(formik.errors.match_result)}
              helperText={formik.touched.match_result && formik.errors.match_result}
            />
            <FormControlLabel
              control={
                <Switch
                  checked={formik.values.lineup_announced}
                  onChange={formik.handleChange}
                  name="lineup_announced"
                />
              }
              label="Lineup Announced"
            />
            <FormControlLabel
              control={
                <Switch
                  checked={formik.values.score_algorithm_enabled}
                  onChange={formik.handleChange}
                  name="score_algorithm_enabled"
                />
              }
              label="Score Algorithm Enabled"
            />
            <FormControlLabel
              control={
                <Switch
                  checked={formik.values.fp_available}
                  onChange={formik.handleChange}
                  name="fp_available"
                />
              }
              label="FP Available"
            />
            <FormControlLabel
              control={
                <Switch
                  checked={formik.values.is_substitute_allowed}
                  onChange={formik.handleChange}
                  name="is_substitute_allowed"
                />
              }
              label="Substitute Allowed"
            />
            <FormControlLabel
              control={
                <Switch
                  checked={formik.values.is_e_coverage}
                  onChange={formik.handleChange}
                  name="is_e_coverage"
                />
              }
              label="E Coverage"
            />
          </DialogContent>
          <DialogActions>
            <Button onClick={handleClose}>Cancel</Button>
            <Button type="submit" variant="contained" color="primary">
              {selectedMatch ? 'Update' : 'Add'}
            </Button>
          </DialogActions>
        </form>
      </Dialog>

      {/* Audit Log Viewer */}
      {auditLogMatch && (
        <AuditLogViewer
          open={auditLogOpen}
          onClose={handleAuditLogClose}
          appName="cricket"
          modelName="match"
          objectId={auditLogMatch.id}
          title={`Audit Log - ${auditLogMatch.name || `Match #${auditLogMatch.id}`}`}
        />
      )}
    </Box>
  );
};

export default Matches;